import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── NAV ITEMS ───────────────────────────────────────────────────────────────
const navItems = [
  { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",      path: "/dashboard" }] },
  { section: "ENGAGE",    items: [
      { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
      { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
      { icon: "ti-users",        label: "Leads",         path: "/leads"         },
  ]},
  { section: "CONFIGURE", items: [
      { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
      { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
      { icon: "ti-settings", label: "Settings",       path: "/settings"  },
  ]},
];

// ─── DESKTOP SIDEBAR ─────────────────────────────────────────────────────────
function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <aside className="hidden md:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}>
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>
      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => (
              <div key={label} onClick={() => navigate(path)}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all
                  ${location.pathname === path
                    ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                    : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}>
                <i className={`ti ${icon} text-base flex-shrink-0`} />
                <span className="text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" /> Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">
          © 2026{" "}
          <a 
            href="https://awktechz.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-orange-500 transition-colors"
          >
            AWK Technologies
          </a>
        </div>
      </div>
    </aside>
  );
}

// ─── MOBILE SLIDE-IN MENU (AWK-style) ────────────────────────────────────────
function MobileMenu({ open, onClose, chatHistory, activeChat, setActiveChat, onNewChat }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 md:hidden ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(2px)" }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 h-full z-50 flex flex-col transition-transform duration-300 ease-out md:hidden`}
        style={{
          width: 300,
          background: "#161412",
          borderRight: "1px solid #2a2825",
          transform: open ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        {/* Menu header */}
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500 hover:text-stone-200 hover:bg-white/5 transition-all">
            <i className="ti ti-x text-lg" />
          </button>
        </div>

        {/* New chat button */}
        <div className="px-4 py-3 border-b" style={{ borderColor: "#2a2825" }}>
          <button onClick={() => { onNewChat(); onClose(); }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold text-white transition-all active:scale-[0.98]"
            style={{ background: "linear-gradient(135deg,#e8602a,#c84a1e)", boxShadow: "0 4px 16px rgba(232,96,42,0.35)" }}>
            <i className="ti ti-plus text-base" />
            New Chat
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Nav sections */}
          <div className="px-4 py-4 border-b" style={{ borderColor: "#2a2825" }}>
            {navItems.map(({ section, items }) => (
              <div key={section} className="mb-4">
                <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-1">{section}</div>
                {items.map(({ icon, label, path }) => (
                  <button key={label} onClick={() => { navigate(path); onClose(); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-left transition-all active:scale-[0.98] ${
                      location.pathname === path
                        ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                        : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                    }`}>
                    <i className={`ti ${icon} text-base flex-shrink-0`} />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Chat History accordion */}
          <div className="px-4 py-3">
            <button onClick={() => setHistoryOpen(h => !h)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-stone-300 hover:bg-white/5 transition-all mb-2">
              <div className="flex items-center gap-2">
                <i className="ti ti-history text-base text-stone-500" />
                <span className="text-sm font-semibold">Chat History</span>
              </div>
              <i className={`ti ti-chevron-down text-stone-600 text-sm transition-transform ${historyOpen ? "rotate-180" : ""}`} />
            </button>

            {historyOpen && (
              <div className="flex flex-col gap-1">
                {chatHistory.map(group => (
                  <div key={group.label} className="mb-3">
                    <div className="text-[10px] text-stone-600 uppercase tracking-widest px-2 mb-1.5">{group.label}</div>
                    {group.items.map(item => (
                      <button key={item.id}
                        onClick={() => { setActiveChat(item); onClose(); }}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all mb-0.5 active:scale-[0.98] ${
                          activeChat?.id === item.id
                            ? "bg-orange-600/15 text-orange-300 border border-orange-600/25"
                            : "text-stone-400 hover:bg-white/[0.04] hover:text-stone-200"
                        }`}>
                        <div className="flex items-start gap-2">
                          <i className="ti ti-message-dots text-xs text-stone-600 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium truncate text-[13px]">{item.title}</div>
                            <div className="text-xs text-stone-600 truncate mt-0.5">{item.time}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-orange-600 flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-200">Ahmad</div>
              <div className="text-[10px] text-stone-600">
                <a 
                  href="https://awktechz.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-orange-500 transition-colors"
                >
                  AWK Technologies
                </a>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-700 mt-3">
            <i className="ti ti-shield-check text-orange-700/50" /> Secured workspace · © 2026{" "}
            <a 
              href="https://awktechz.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors"
            >
              AWK Technologies
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MOBILE TOPBAR ───────────────────────────────────────────────────────────
function MobileTopbar({ onMenuOpen, onNewChat, currentModel, activeChat, onLogout }) {
  return (
    <header className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0 md:hidden"
      style={{ background: "#161412", borderColor: "#2a2825",
        paddingTop: "max(12px, env(safe-area-inset-top, 12px))" }}>
      {/* Hamburger */}
      <button onClick={onMenuOpen}
        className="w-9 h-9 rounded-xl flex items-center justify-center text-stone-400 hover:text-white hover:bg-white/5 transition-all flex-shrink-0"
        style={{ border: "1px solid #2a2825" }}>
        <i className="ti ti-menu-2 text-lg" />
      </button>

      {/* Brand + model pill */}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ti ti-bolt text-white text-xs" />
          </div>
          <span className="text-sm font-bold text-stone-100 truncate">Nigella VOX</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-full px-2.5 py-1 flex-shrink-0"
          style={{ background: "#e8602a18", border: "1px solid #e8602a30" }}>
          <i className="ti ti-robot text-orange-500 text-[10px]" />
          <span className="text-[10px] font-semibold text-orange-400">
            {currentModel === "nigella-v2.1" ? "v2.1" : currentModel === "nigella-fast" ? "Fast" : "Code"}
          </span>
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button onClick={onNewChat}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-stone-400 hover:text-orange-400 transition-all"
          style={{ border: "1px solid #2a2825" }}>
          <i className="ti ti-plus text-base" />
        </button>
        <button onClick={onLogout}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
          style={{ background: "#e8602a" }}>
          <i className="ti ti-power text-sm" />
        </button>
      </div>
    </header>
  );
}

// ─── DESKTOP TOPBAR ──────────────────────────────────────────────────────────
function DesktopTopbar({ onNewChat, currentModel, setCurrentModel, onLogout }) {
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const models = [
    { id: "nigella-v2.1", name: "Nigella VOX v2.1", desc: "Most capable model" },
    { id: "nigella-fast", name: "Nigella Fast",     desc: "Faster responses"   },
    { id: "nigella-code", name: "Nigella Code",     desc: "Optimized for coding"},
  ];
  return (
    <header className="hidden md:flex items-center gap-4 px-6 py-3 border-b flex-shrink-0 relative"
      style={{ background: "#161412", borderColor: "#2a2825" }}>
      {/* Model selector */}
      <div className="relative">
        <button onClick={() => setShowModelMenu(!showModelMenu)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-stone-200 hover:bg-white/5 transition-colors border"
          style={{ borderColor: "#2a2825" }}>
          <i className="ti ti-robot text-orange-500" />
          <span>{models.find(m => m.id === currentModel)?.name || "Nigella VOX v2.1"}</span>
          <i className="ti ti-chevron-down text-xs text-stone-500" />
        </button>
        {showModelMenu && (
          <div className="absolute top-full left-0 mt-1 w-72 rounded-2xl border shadow-2xl z-50 py-2"
            style={{ background: "#1a1815", borderColor: "#2a2825" }}>
            <div className="px-4 py-2 border-b" style={{ borderColor: "#2a2825" }}>
              <span className="text-xs font-semibold text-stone-200">Choose Model</span>
            </div>
            {models.map(m => (
              <button key={m.id} onClick={() => { setCurrentModel(m.id); setShowModelMenu(false); }}
                className={`w-full text-left px-4 py-3 hover:bg-white/5 flex items-center gap-3 ${currentModel === m.id ? "bg-orange-600/10" : ""}`}>
                <i className="ti ti-robot text-orange-500" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-stone-200">{m.name}</div>
                  <div className="text-xs text-stone-500">{m.desc}</div>
                </div>
                {currentModel === m.id && <i className="ti ti-check text-orange-500 text-sm" />}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* New chat */}
      <button onClick={onNewChat}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-stone-300 hover:text-white hover:bg-white/5 border"
        style={{ borderColor: "#2a2825" }}>
        <i className="ti ti-plus text-sm" /> New Chat
      </button>
      {/* Search */}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2 border ml-auto"
        style={{ background: "#1e1c1a", borderColor: "#2a2825", width: 280 }}>
        <i className="ti ti-search text-stone-600 text-xs" />
        <input className="bg-transparent text-sm text-stone-300 placeholder-stone-600 outline-none flex-1" placeholder="Search conversations..." />
      </div>
      <button className="w-9 h-9 rounded-xl flex items-center justify-center text-stone-500 hover:text-stone-300 hover:bg-white/5">
        <i className="ti ti-settings text-sm" />
      </button>
      {/* Logout */}
      <div className="relative">
        <button onMouseEnter={() => setShowLogout(true)} onMouseLeave={() => setShowLogout(false)}
          onClick={onLogout}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all"
          style={{ background: "#e8602a" }}>
          <i className="ti ti-power text-lg" />
        </button>
        {showLogout && (
          <div onMouseEnter={() => setShowLogout(true)} onMouseLeave={() => setShowLogout(false)}
            className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
            style={{ background: "#1a1815", borderColor: "#2a2825" }}>
            <button onClick={onLogout}
              className="w-full px-4 py-2.5 text-left text-sm text-stone-300 hover:bg-orange-500/10 hover:text-orange-400 flex items-center gap-2">
              <i className="ti ti-logout text-orange-500" /> <span className="font-medium">Logout</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

// ─── HISTORY DATA ────────────────────────────────────────────────────────────
const historyGroups = [
  { label: "Today", items: [
    { id:1, title:"Customer lead scoring strategy", preview:"How can I automate lead scoring?", time:"2h ago", pinned:false },
    { id:2, title:"Email automation workflow",       preview:"Create email sequence for subscribers", time:"4h ago", pinned:true },
    { id:3, title:"Knowledge base optimization",    preview:"Improve FAQ section for support", time:"6h ago", pinned:false },
  ]},
  { label: "Yesterday", items: [
    { id:4, title:"Integration troubleshooting", preview:"Debug WhatsApp webhook issues", time:"1d ago", pinned:false },
    { id:5, title:"Conversation analytics",     preview:"Analyze chat performance metrics", time:"1d ago", pinned:false },
  ]},
  { label: "Previous 7 days", items: [
    { id:6, title:"Sales funnel optimization",     preview:"Improve conversion rates", time:"3d ago", pinned:false },
    { id:7, title:"Customer sentiment analysis",   preview:"Understand feedback patterns", time:"5d ago", pinned:false },
    { id:8, title:"AI model fine-tuning",          preview:"Customize responses for industry", time:"6d ago", pinned:false },
  ]},
];

// ─── DESKTOP HISTORY PANEL ───────────────────────────────────────────────────
function HistoryPanel({ activeChat, setActiveChat, onNewChat, searchTerm, setSearchTerm }) {
  const [hoveredChat, setHoveredChat] = useState(null);
  const filtered = historyGroups.map(g => ({
    ...g,
    items: g.items.filter(i =>
      i.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.preview.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(g => g.items.length > 0);

  return (
    <div className="hidden md:flex flex-col h-full border-l flex-shrink-0"
      style={{ width: 300, background: "#161412", borderColor: "#2a2825" }}>
      <div className="flex items-center justify-between px-4 py-4 border-b" style={{ borderColor: "#2a2825" }}>
        <h2 className="text-sm font-semibold text-stone-200">Chat History</h2>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-300 hover:bg-white/5">
            <i className="ti ti-search text-xs" />
          </button>
          <button onClick={onNewChat}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-300 hover:text-white hover:bg-orange-600/20">
            <i className="ti ti-plus text-sm" />
          </button>
        </div>
      </div>
      <div className="px-4 py-3 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-2 rounded-xl px-3 py-2 border"
          style={{ background: "#1e1c1a", borderColor: "#2a2825" }}>
          <i className="ti ti-search text-stone-600 text-xs" />
          <input className="bg-transparent text-sm text-stone-400 placeholder-stone-600 outline-none w-full"
            placeholder="Search conversations..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {filtered.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <i className="ti ti-search text-2xl text-stone-600 mb-2 block" />
            <div className="text-sm text-stone-500">No conversations found</div>
          </div>
        ) : filtered.map(({ label, items }) => (
          <div key={label} className="mb-6">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider px-4 mb-3">{label}</div>
            {items.map(item => (
              <div key={item.id} className="relative group mx-2"
                onMouseEnter={() => setHoveredChat(item.id)}
                onMouseLeave={() => setHoveredChat(null)}>
                <button onClick={() => setActiveChat(item)}
                  className={`w-full text-left px-3 py-3 rounded-xl transition-all flex items-start gap-3 ${
                    activeChat?.id === item.id
                      ? "bg-orange-600/15 text-orange-300 border border-orange-600/25"
                      : "text-stone-400 hover:bg-white/[0.04] hover:text-stone-200"
                  }`}>
                  <i className="ti ti-message-dots text-xs flex-shrink-0 mt-0.5 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium truncate">{item.title}</div>
                      {item.pinned && <i className="ti ti-pin text-xs text-orange-500 flex-shrink-0" />}
                    </div>
                    <div className="text-xs text-stone-600 truncate mt-1">{item.preview}</div>
                    <div className="text-xs text-stone-700 mt-1">{item.time}</div>
                  </div>
                </button>
                {hoveredChat === item.id && (
                  <div className="absolute right-2 top-2 flex items-center gap-1 bg-stone-800 rounded-lg px-1 py-1">
                    <button className="w-6 h-6 rounded flex items-center justify-center text-stone-500 hover:text-orange-400">
                      <i className={`ti ${item.pinned ? "ti-pin-filled" : "ti-pin"} text-xs`} />
                    </button>
                    <button className="w-6 h-6 rounded flex items-center justify-center text-stone-500 hover:text-red-400">
                      <i className="ti ti-trash text-xs" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="px-4 py-3 border-t" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-orange-600 flex-shrink-0"
            style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)" }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-stone-200 truncate">Ahmad</div>
            <div className="text-xs text-stone-600 truncate">
              <a 
                href="https://awktechz.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition-colors"
              >
                AWK Technologies
              </a>
            </div>
          </div>
          <button className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-300">
            <i className="ti ti-dots text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MESSAGE BUBBLE ──────────────────────────────────────────────────────────
function MessageBubble({ role, text, time, isStreaming = false, onRegenerate, onCopy, onEdit }) {
  const [showActions, setShowActions] = useState(false);
  const [copied, setCopied] = useState(false);
  const isUser = role === "user";
  const isAssistant = role === "assistant";

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  return (
    <div className={`flex gap-2 md:gap-3 ${isUser ? "flex-row-reverse" : "flex-row"} group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}>
      {/* Avatar */}
      <div className="flex-shrink-0 mt-0.5">
        {isUser ? (
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-orange-500 flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
            style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)" }}>A</div>
        ) : (
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 border border-orange-600/30"
            style={{ background: "linear-gradient(135deg,#1d1b19,#2a2825)" }}>
            <i className="ti ti-robot text-orange-500 text-xs md:text-sm" />
          </div>
        )}
      </div>

      {/* Bubble */}
      <div className={`flex flex-col gap-1 max-w-[85%] md:max-w-[75%] ${isUser ? "items-end" : "items-start"}`}>
        <div className="relative">
          <div className={`px-3 md:px-4 py-2.5 md:py-3 rounded-2xl text-sm leading-relaxed relative ${
              isUser ? "text-white rounded-br-sm" : "text-stone-200 rounded-bl-sm border"
            } ${isStreaming ? "animate-pulse" : ""}`}
            style={isUser ? { background: "#e8602a" } : { background: "#1d1b19", borderColor: "#2a2825" }}>
            <div className="whitespace-pre-wrap">{text}</div>
            {isStreaming && (
              <div className="inline-flex items-center gap-1 ml-2">
                {[0,150,300].map(d => (
                  <div key={d} className="w-1 h-1 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay:`${d}ms` }} />
                ))}
              </div>
            )}
          </div>
          {/* Action buttons — desktop hover */}
          {showActions && !isStreaming && (
            <div className={`hidden md:flex absolute top-0 ${isUser ? "right-full mr-2" : "left-full ml-2"} items-center gap-1`}>
              <button onClick={handleCopy}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-300"
                style={{ background: "#1a1815", border: "1px solid #2a2825" }}>
                <i className={`ti ${copied ? "ti-check" : "ti-copy"} text-xs`} />
              </button>
              {isAssistant && (
                <button onClick={onRegenerate}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-300"
                  style={{ background: "#1a1815", border: "1px solid #2a2825" }}>
                  <i className="ti ti-refresh text-xs" />
                </button>
              )}
              {isUser && (
                <button onClick={onEdit}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-300"
                  style={{ background: "#1a1815", border: "1px solid #2a2825" }}>
                  <i className="ti ti-edit text-xs" />
                </button>
              )}
            </div>
          )}
        </div>
        {/* Mobile action row below bubble */}
        {!isStreaming && (
          <div className={`flex md:hidden items-center gap-2 px-1 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
            <button onClick={handleCopy}
              className="flex items-center gap-1 text-[10px] text-stone-600 active:text-orange-400 transition-colors">
              <i className={`ti ${copied ? "ti-check text-emerald-400" : "ti-copy"} text-xs`} />
              {copied ? <span className="text-emerald-400">Copied</span> : null}
            </button>
            {isAssistant && (
              <button onClick={onRegenerate}
                className="text-[10px] text-stone-600 active:text-orange-400 transition-colors">
                <i className="ti ti-refresh text-xs" />
              </button>
            )}
            <span className="text-[10px] text-stone-700">{time}</span>
          </div>
        )}
        <span className="hidden md:block text-xs text-stone-600 px-1 opacity-0 group-hover:opacity-100 transition-opacity">{time}</span>
      </div>
    </div>
  );
}

// ─── TYPING INDICATOR ────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex gap-2 md:gap-3">
      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 border border-orange-600/30"
        style={{ background: "linear-gradient(135deg,#1d1b19,#2a2825)" }}>
        <i className="ti ti-robot text-orange-500 text-xs md:text-sm" />
      </div>
      <div className="flex items-center gap-1.5 px-4 py-3 rounded-2xl rounded-bl-sm border"
        style={{ background: "#1d1b19", borderColor: "#2a2825" }}>
        {[0,150,300].map(d => <div key={d} className="w-2 h-2 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay:`${d}ms` }} />)}
      </div>
    </div>
  );
}

// ─── MOBILE INPUT BAR ────────────────────────────────────────────────────────
function MobileInputBar({ input, setInput, onSend, typing, textareaRef }) {
  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  }
  function adjustH(e) {
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  }
  return (
    <div className="flex-shrink-0 px-3 py-3 md:hidden border-t"
      style={{ background: "#111010", borderColor: "#2a2825",
        paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}>
      <div className="flex items-end gap-2 rounded-2xl px-3 py-2.5 border"
        style={{ background: "#1a1815", borderColor: input.trim() ? "rgba(232,96,42,0.5)" : "#2a2825",
          boxShadow: input.trim() ? "0 0 0 2px rgba(232,96,42,0.1)" : "none" }}>
        {/* Attach */}
        <button className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500 hover:text-orange-400 flex-shrink-0 mb-0.5">
          <i className="ti ti-paperclip text-base" />
        </button>
        {/* Textarea */}
        <textarea ref={textareaRef} value={input}
          onChange={e => { setInput(e.target.value); adjustH(e); }}
          onKeyDown={handleKey}
          placeholder="Message Nigella VOX…"
          rows={1}
          className="flex-1 bg-transparent text-sm text-white placeholder-stone-600 outline-none resize-none leading-relaxed py-1"
          style={{ minHeight: 24, maxHeight: 120 }} />
        {/* Send */}
        <button onClick={onSend} disabled={!input.trim() || typing}
          className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mb-0.5 transition-all active:scale-95 ${
            input.trim() && !typing ? "text-white" : "text-stone-600"}`}
          style={{ background: input.trim() && !typing ? "#e8602a" : "#252320" }}>
          {typing
            ? <div className="w-3.5 h-3.5 border-2 border-stone-400 border-t-orange-400 rounded-full animate-spin" />
            : <i className="ti ti-arrow-up text-sm" />}
        </button>
      </div>
      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[10px] text-stone-700">⏎ Send · ⇧⏎ New line</span>
        <span className={`text-[10px] font-mono ${input.length > 3800 ? "text-orange-400" : "text-stone-700"}`}>
          {input.length}/4,000
        </span>
      </div>
    </div>
  );
}

// ─── DESKTOP INPUT BAR ───────────────────────────────────────────────────────
function DesktopInputBar({ input, setInput, onSend, typing, textareaRef, currentModel }) {
  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  }
  function adjustH(e) {
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 240) + "px";
  }
  return (
    <div className="hidden md:block flex-shrink-0 px-6 pb-6 pt-4" style={{ background: "#111010" }}>
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden" style={{ padding: "2px" }}>
          <div className={`absolute inset-0 rounded-3xl transition-all duration-300 ${input.trim() ? "opacity-100" : "opacity-0"}`}
            style={{ background: "linear-gradient(145deg,#e8602a,#c84a1e)" }} />
          <div className="relative rounded-3xl border transition-all duration-300"
            style={{ background: input.trim() ? "rgba(26,24,21,0.95)" : "rgba(33,33,33,0.8)",
              borderColor: input.trim() ? "rgba(232,96,42,0.5)" : "rgba(64,64,64,0.6)",
              boxShadow: input.trim() ? "0 10px 40px -10px rgba(232,96,42,0.25)" : "0 8px 32px -8px rgba(0,0,0,0.4)" }}>
            <div className="flex items-end gap-4 px-6 py-4">
              <button className="group w-9 h-9 rounded-xl flex items-center justify-center text-stone-400 hover:text-white hover:bg-orange-600/20 border border-transparent hover:border-orange-600/30 transition-all">
                <i className="ti ti-paperclip text-lg group-hover:rotate-12 transition-transform" />
              </button>
              <div className="flex-1 overflow-hidden">
                <textarea ref={textareaRef} value={input}
                  onChange={e => { setInput(e.target.value); adjustH(e); }}
                  onKeyDown={handleKey}
                  placeholder="Message Nigella VOX..."
                  rows={1}
                  className="w-full bg-transparent text-[15px] text-white placeholder-stone-400 outline-none resize-none leading-7 py-1.5"
                  style={{ minHeight: 28, maxHeight: 240 }} />
              </div>
              <button onClick={onSend} disabled={!input.trim() || typing}
                className={`relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  input.trim() && !typing
                    ? "bg-orange-600 hover:bg-orange-500 text-white shadow-lg transform hover:scale-105 border border-orange-500"
                    : "bg-stone-800 text-stone-500 cursor-not-allowed border border-stone-700"
                }`}>
                {typing
                  ? <div className="w-4 h-4 border-2 border-stone-300 border-t-orange-400 rounded-full animate-spin" />
                  : <i className="ti ti-arrow-up text-base font-bold" />}
                {input.trim() && !typing && <div className="absolute inset-0 rounded-xl bg-orange-500/20 animate-pulse" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 px-4">
          <div className="flex items-center gap-6">
            <span className="text-xs text-stone-500">Nigella VOX can make mistakes. Verify important information.</span>
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-stone-600">
              <kbd className="px-1.5 py-0.5 text-[10px] bg-stone-800 border border-stone-700 rounded-md font-mono">⏎</kbd> to send
              <span className="mx-1">·</span>
              <kbd className="px-1.5 py-0.5 text-[10px] bg-stone-800 border border-stone-700 rounded-md font-mono">⇧⏎</kbd> new line
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className={`font-mono ${input.length > 3800 ? "text-orange-400" : "text-stone-500"}`}>{input.length}/4,000</span>
            <div className="flex items-center gap-1.5 text-stone-600">
              <i className="ti ti-robot text-xs" />
              <span>{currentModel === "nigella-v2.1" ? "v2.1" : currentModel === "nigella-fast" ? "Fast" : "Code"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CHAT MAIN ───────────────────────────────────────────────────────────────
function ChatMain({ activeChat, currentModel }) {
  const [input, setInput]       = useState("");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping]     = useState(false);
  const [started, setStarted]   = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const bottomRef  = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing, streamingText]);

  useEffect(() => {
    if (activeChat) {
      setStarted(true);
      setMessages([
        { id:1, role:"user",      text:activeChat.preview, time:activeChat.time },
        { id:2, role:"assistant", text:`I understand you're asking about "${activeChat.title}". Let me help you with that.\n\nBased on your Nigella VOX ecosystem, I can provide tailored assistance. What specific aspect would you like me to focus on?`, time:"Just now" },
      ]);
    } else { setMessages([]); setStarted(false); }
  }, [activeChat]);

  const now = () => new Date().toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true });

  function simulateStreaming(fullText) {
    setStreamingText(""); setTyping(true);
    let cur = "";
    const words = fullText.split(" ");
    words.forEach((w, i) => {
      setTimeout(() => {
        cur += (i === 0 ? "" : " ") + w;
        setStreamingText(cur);
        if (i === words.length - 1) {
          setTimeout(() => {
            setTyping(false); setStreamingText("");
            setMessages(p => [...p, { id:Date.now(), role:"assistant", time:now(), text:fullText }]);
          }, 100);
        }
      }, i * 50);
    });
  }

  function send(text) {
    const trimmed = (text || input).trim();
    if (!trimmed) return;
    setStarted(true);
    setMessages(p => [...p, { id:Date.now(), role:"user", text:trimmed, time:now() }]);
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    const responses = [
      `I understand your request about "${trimmed}". Here's how I can help:\n\n**Recommended approach:**\n1. **Analyse** your current setup\n2. **Strategy** development\n3. **Implementation** guidance\n4. **Optimisation** & monitoring\n\nWould you like me to elaborate on any of these?`,
      `Great question! Based on your Nigella VOX ecosystem:\n\n✅ **Lead Management** — automated scoring and nurturing\n✅ **Conversation Analytics** — real-time insights\n✅ **Integration Setup** — connect existing tools\n✅ **Workflow Optimisation** — streamline processes\n\nWhat would you like to focus on?`,
    ];
    setTimeout(() => simulateStreaming(responses[Math.floor(Math.random() * responses.length)]), 800);
  }

  const suggestions = [
    { text:"How can I improve my lead conversion rate?",    icon:"ti-trending-up"    },
    { text:"Analyse my recent conversation metrics",         icon:"ti-chart-bar"      },
    { text:"Set up automated email sequences",              icon:"ti-mail-forward"   },
    { text:"Help me optimise my knowledge base",            icon:"ti-book"           },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ background: "#111010" }}>
      {/* Scroll area */}
      <div className="flex-1 overflow-y-auto">
        {!started ? (
          <div className="flex flex-col items-center justify-center min-h-full px-4 md:px-8 py-8 md:py-12 gap-6 md:gap-10">
            <div className="flex flex-col items-center gap-3 md:gap-4 text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-orange-600 flex items-center justify-center"
                style={{ boxShadow: "0 0 40px rgba(232,96,42,0.35)" }}>
                <i className="ti ti-robot text-white text-2xl md:text-3xl" />
              </div>
              <div>
                <h1 className="text-2xl md:text-4xl font-extrabold text-white tracking-tight mb-2">
                  How can I assist you?
                </h1>
                <p className="text-stone-500 text-sm md:text-base max-w-sm md:max-w-md">
                  Ask me anything about your business, leads, conversations, or platform optimisation.
                </p>
              </div>
            </div>
            {/* Suggestion grid — 1 col on mobile, 2 on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3 w-full max-w-sm md:max-w-2xl">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => { setInput(s.text); textareaRef.current?.focus(); }}
                  className="flex items-center gap-3 rounded-2xl p-3.5 md:p-4 border text-left transition-all active:scale-[0.98] hover:border-orange-500/40 hover:bg-white/[0.025]"
                  style={{ background: "#1a1815", borderColor: "#2a2825" }}>
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "#e8602a18" }}>
                    <i className={`ti ${s.icon} text-orange-500 text-sm md:text-base`} />
                  </div>
                  <span className="text-sm text-stone-300">{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-3 md:px-6 py-6 md:py-8 flex flex-col gap-4 md:gap-6">
            {messages.map((m, i) => (
              <MessageBubble key={m.id} {...m}
                onRegenerate={() => {}}
                onCopy={() => {}}
                onEdit={() => {}} />
            ))}
            {typing && <MessageBubble role="assistant" text={streamingText} time="" isStreaming />}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
      {/* Mobile input */}
      <MobileInputBar input={input} setInput={setInput} onSend={() => send()} typing={typing} textareaRef={textareaRef} />
      {/* Desktop input */}
      <DesktopInputBar input={input} setInput={setInput} onSend={() => send()} typing={typing} textareaRef={textareaRef} currentModel={currentModel} />
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function AIAgentPage({ onLogout }) {
  const [activeChat, setActiveChat]     = useState(null);
  const [currentModel, setCurrentModel] = useState("nigella-v2.1");
  const [searchTerm, setSearchTerm]     = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  function handleNewChat() { setActiveChat(null); }

  return (
    <div className="flex flex-col md:flex-row h-screen w-screen overflow-hidden" style={{ background: "#111010" }}>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile slide-in menu */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        chatHistory={historyGroups}
        activeChat={activeChat}
        setActiveChat={setActiveChat}
        onNewChat={handleNewChat}
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <MobileTopbar
          onMenuOpen={() => setMobileMenuOpen(true)}
          onNewChat={handleNewChat}
          currentModel={currentModel}
          activeChat={activeChat}
          onLogout={onLogout}
        />

        {/* Desktop topbar */}
        <DesktopTopbar
          onNewChat={handleNewChat}
          currentModel={currentModel}
          setCurrentModel={setCurrentModel}
          onLogout={onLogout}
        />

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Chat */}
          <ChatMain activeChat={activeChat} currentModel={currentModel} />
          {/* Desktop history panel */}
          <HistoryPanel
            activeChat={activeChat}
            setActiveChat={setActiveChat}
            onNewChat={handleNewChat}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </div>
      </div>
    </div>
  );
}